// Brand Controls redesign — shell.
//
// Header + UnderlineTabs + view routing + the shared empty-state. All state
// and handlers live in the orchestrator (brand-voice-profile.tsx); this is
// presentational. Padding matches the app convention (px-4 md:px-8), not
// the DC's centered 1180 clamp, so it lines up with the other modules.

import { Layers, Check } from "lucide-react";
import { UnderlineTabs, UnderlineTabsPanel } from "../../ui/underline-tabs";
import { EmptyState } from "./shared";
import { OverviewView, type OverviewViewProps } from "./overview-view";
import { QuickSetupView, type QuickSetupViewProps } from "./quick-setup-view";
import { AdvancedView, type AdvancedViewProps } from "./advanced-view";
import { HistoryView, type HistoryViewProps } from "./history-view";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "setup", label: "Quick Setup" },
  { value: "advanced", label: "Advanced Editing" },
  { value: "history", label: "Version History" },
];

export interface BrandControlsShellProps {
  activeTab: string;
  setActiveTab: (t: string) => void;
  hasProfile: boolean;
  brand: string;
  headerVersion: string;
  headerUpdatedAt: string;
  headerUsageCount: number;
  goSetup: () => void;
  overview: OverviewViewProps;
  quickSetup: QuickSetupViewProps;
  advanced: AdvancedViewProps;
  history: HistoryViewProps;
}

// Match the app gutter (Deep Signal / Brand Monitoring land at 64px on
// desktop). px-6 md:px-16 = 24px mobile / 64px desktop.
const GUTTER = "px-6 md:px-16";
const PANEL_PAD = `${GUTTER} py-6 md:py-8`;

export function BrandControlsShell(props: BrandControlsShellProps) {
  const { activeTab, setActiveTab, hasProfile, brand, headerVersion, headerUpdatedAt, headerUsageCount, goSetup } = props;

  return (
    <div style={{ backgroundColor: "var(--background)" }}>
      {/* header */}
      <div className={`${GUTTER} py-6 md:py-8 border-b`} style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 16, fontSize: 13 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--muted-foreground)", fontWeight: 500 }}>
            <Layers style={{ width: 15, height: 15 }} strokeWidth={1.7} />
            Optimize
          </span>
          <span style={{ color: "var(--silver-mist)" }}>/</span>
          <span style={{ color: "var(--foreground)", fontWeight: 600 }}>Brand Controls</span>
        </div>
        <h1
          style={{
            fontSize: "clamp(24px,3.5vw,30px)",
            fontWeight: 600,
            letterSpacing: "-.02em",
            lineHeight: 1.25,
            color: "var(--foreground)",
            margin: "0 0 8px",
            overflowWrap: "anywhere",
          }}
        >
          Teach AI your voice. Every fix sounds like {brand}.
        </h1>
        <p style={{ fontSize: 15, color: "var(--muted-foreground)", margin: "0 0 18px", textWrap: "pretty" }}>
          This is the voice every generated asset inherits, across Action Items and Test &amp; Refine. Set it once and AI writes as you, not as a generic model.
        </p>
        {hasProfile && (
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: "var(--signal-green)", flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>Active profile v{headerVersion}</span>
            </span>
            {headerUpdatedAt && headerUpdatedAt !== "—" && (
              <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>Updated {headerUpdatedAt}</span>
            )}
            {headerUsageCount > 0 && (
              <span style={{ fontSize: 12, color: "var(--muted-foreground)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Check style={{ width: 13, height: 13, color: "var(--muted-foreground)" }} strokeWidth={1.7} />
                {headerUsageCount} assets generated with this voice
              </span>
            )}
          </div>
        )}
      </div>

      {/* tabs + panels */}
      <UnderlineTabs tabs={TABS} value={activeTab} onChange={setActiveTab} padding={GUTTER}>
        <UnderlineTabsPanel value="overview">
          <div className={PANEL_PAD}>
            {hasProfile ? (
              <OverviewView {...props.overview} />
            ) : (
              <EmptyState
                headline={`No voice yet. Let's teach AI how ${brand} sounds.`}
                body="Point me at your site and I'll read your live content, detect how you actually write, and draft a profile in about 30 seconds. Every asset AI generates will inherit it."
                onExtract={goSetup}
              />
            )}
          </div>
        </UnderlineTabsPanel>

        <UnderlineTabsPanel value="setup">
          <div className={PANEL_PAD}>
            <QuickSetupView {...props.quickSetup} />
          </div>
        </UnderlineTabsPanel>

        <UnderlineTabsPanel value="advanced">
          <div className={PANEL_PAD}>
            {hasProfile ? (
              <AdvancedView {...props.advanced} />
            ) : (
              <EmptyState
                headline="Nothing to fine-tune yet"
                body="Extract a voice first, then come back here to adjust each attribute, edit the avoid list, and test how it reads."
                onExtract={goSetup}
              />
            )}
          </div>
        </UnderlineTabsPanel>

        <UnderlineTabsPanel value="history">
          <div className={PANEL_PAD}>
            {hasProfile ? (
              <HistoryView {...props.history} />
            ) : (
              <EmptyState
                headline="No versions yet"
                body="Once you extract and save a voice, every version shows up here so you can compare changes or roll back to an earlier one."
                onExtract={goSetup}
              />
            )}
          </div>
        </UnderlineTabsPanel>
      </UnderlineTabs>
    </div>
  );
}
